import type { UserGeo } from '#shared/utils/resolveUserGeo'

/**
 * In-flight promise memo, module-scoped on purpose.
 *
 * All (currently six planned) call sites can mount in the same tick. Memoising
 * the *resolved value* would still be empty when the second caller runs, giving
 * one request per consumer; memoising the *promise* collapses them to a single
 * `/api/geo` request per full page load. Nuxt client-side route changes do not
 * reset module state, so browsing around costs nothing further.
 *
 * Module scope is safe ONLY because this composable is client-only (see the
 * `import.meta.client` guard). On the server it would be a cross-request leak —
 * the same failure class as the SSR-geo trap the endpoint exists to avoid.
 */
let geoPromise: Promise<UserGeo | null> | null = null

/**
 * Resolve the viewer's coordinates for Typesense geo ranking (KFT-4027).
 *
 * Client-only and never fired during SSR. Every consumer must tolerate `null`
 * (geo unavailable, request failed, or running on the server).
 */
export const useUserGeo = () => {
  const getUserGeo = (): Promise<UserGeo | null> => {
    // KFT-3753 guard: never resolve geo during SSR.
    if (!import.meta.client) return Promise.resolve(null)

    if (!geoPromise) {
      // Trailing slash matches the repo's API-client convention and the SEO
      // canonicalization (cleanUrl appends one). Without it this GET 301s to
      // `/api/geo/` in prod/homolog, and that redirect misses the dedicated
      // CloudFront `/api/geo` behaviour, stripping the geo headers. KFT-4027.
      geoPromise = $fetch<UserGeo | null>('/api/geo/')
        .then((geo) => {
          // KFT-4116 debug: surface which city the /api/geo response resolved
          // to, so geo-driven Typesense ranking is verifiable in the console.
          console.log('[useUserGeo] resolved geo city:', geo?.city ?? null)
          return geo
        })
        .catch((error) => {
          console.error('[useUserGeo] geo resolution failed', error)
          return null
        })
    }

    return geoPromise
  }

  return { getUserGeo }
}
